import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { Customer } from '@/utils/types';
import { supabase } from '@/lib/supabase';
import { submitLeadIntake } from '@/lib/lead-intake';

interface CustomerStore {
  customer: Customer | null;
  likes: string[];
  appointmentTokens: string[];
  isModalOpen: boolean;
  setIsModalOpen: (isOpen: boolean) => void;
  addAppointmentToken: (token: string) => void;
  removeAppointmentToken: (token: string) => void;
  initializeCustomer: (
    customerData?: Partial<Customer>,
    opts?: { leadId?: number | string | null }
  ) => Promise<Customer>;
  toggleLike: (vehicleId: string) => Promise<void>;
  getLikes: () => string[];
}

const useCustomerStore = create(
  persist<CustomerStore>(
    (set, get) => ({
      customer: null,
      likes: [],
      appointmentTokens: [],
      isModalOpen: false,

      setIsModalOpen: (isOpen) => set({ isModalOpen: isOpen }),

      addAppointmentToken: (token) =>
        set((state) => ({
          appointmentTokens: state.appointmentTokens.includes(token)
            ? state.appointmentTokens
            : [...state.appointmentTokens, token],
        })),

      removeAppointmentToken: (token) =>
        set((state) => ({
          appointmentTokens: state.appointmentTokens.filter((t) => t !== token),
        })),

      initializeCustomer: async (customerData, opts) => {
        if (!customerData?.email) {
          throw new Error('El email es requerido');
        }
        if (customerData.client_id == null) {
          throw new Error('client_id es requerido');
        }

        // El dedupe/upsert (por email + client_id) y el link de lead ahora
        // corren server-side en la Edge Function public-lead-intake — el sitio
        // ya no toca customers/leads directo (la RLS los cierra para anon).
        const result = await submitLeadIntake({
          clientId: customerData.client_id as number,
          email: customerData.email,
          customer: {
            first_name: customerData.first_name,
            last_name: customerData.last_name,
            phone: customerData.phone,
            rut: customerData.rut,
            birth_date: customerData.birth_date,
            address: (customerData as Partial<Customer>).address,
          },
          leadId: opts?.leadId ?? null,
        });

        if (!result.customer_id) {
          throw new Error(result.error || 'No se pudo guardar el cliente');
        }

        const savedCustomer = {
          ...(customerData as Customer),
          id: result.customer_id as unknown as Customer['id'],
        } as Customer;
        set({ customer: savedCustomer, isModalOpen: false });
        return savedCustomer;
      },

      toggleLike: async (vehicleId) => {
        const { customer, likes } = get();
        const newLikes = likes.includes(vehicleId)
          ? likes.filter((id) => id !== vehicleId)
          : [...likes, vehicleId];

        if (!customer) {
          set({ isModalOpen: true });
          return;
        }

        set({ likes: newLikes, isModalOpen: false });

        // Sincronizar con Supabase
        if (newLikes.includes(vehicleId)) {
          await supabase.from('vehicles_likes').insert([
            {
              vehicle_id: vehicleId,
              customer_id: customer.id,
            },
          ]);
        } else {
          await supabase
            .from('vehicles_likes')
            .delete()
            .match({ vehicle_id: vehicleId, customer_id: customer.id });
        }
      },

      getLikes: () => get().likes,
    }),
    {
      name: 'customer-storage',
    }
  )
);

export default useCustomerStore;
